/**
 * SavedChatsSidebar component displays a sliding panel with saved chat sessions.
 * Shows a list of sessions sorted by most recent, with delete functionality.
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  ListItemIcon,
  CircularProgress,
  Alert,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { ReactComponent as FolderIcon } from "../../assets/icons/Folder-Dark.svg";
import { useSavedChats } from "../../hooks/useSavedChats";
import type { SessionSummary } from "../../types/api";

/**
 * Props interface for the SavedChatsSidebar component.
 * @interface SavedChatsSidebarProps
 * @property {boolean} isOpen - Whether the sidebar is open.
 * @property {() => void} onClose - Callback to close the sidebar.
 * @property {boolean} [isGenerating] - Whether a response is currently being generated.
 * @property {() => void} onDeleteCurrentChat - Callback to delete the current chat.
 */
interface SavedChatsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isGenerating?: boolean;
  onDeleteCurrentChat: () => void;
}

/**
 * SavedChatsSidebar component.
 * Renders a sliding panel from the right showing saved chat sessions.
 */
const SavedChatsSidebar: React.FC<SavedChatsSidebarProps> = ({
  isOpen,
  onClose,
  isGenerating = false,
  onDeleteCurrentChat,
}) => {
  const posthog = usePostHog();
  const { sessions, isLoading, error, loadSessions, handleDelete } =
    useSavedChats();

  const [searchParams, setSearchParams] = useSearchParams();
  const currentSessionId = searchParams.get("sessionId");

  // Menu state for the three-dot menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null
  );

  // Confirmation modal state for switching sessions while generating
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const isConfirmModalOpen = Boolean(pendingSessionId);

  // Delete confirmation modal state
  const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null);
  const isDeleteModalOpen = Boolean(deleteSessionId);

  const isMenuOpen = Boolean(menuAnchorEl);

  /**
   * Navigates to a session by adding sessionId to URL query params.
   * Preserves existing params like ?mode=agent.
   * If a response is currently being generated, shows a confirmation modal first.
   */
  const handleNavigateToSession = (sessionId: string) => {
    if (isGenerating) {
      setPendingSessionId(sessionId);
      return;
    }
    navigateToSession(sessionId);
  };

  /**
   * Performs the actual navigation to a session.
   */
  const navigateToSession = (sessionId: string) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("sessionId", sessionId);
        return newParams;
      },
      { replace: false }
    );
    onClose();
  };

  /**
   * Confirms navigation to the pending session, discarding the in-progress response.
   */
  const handleConfirmSwitch = () => {
    if (pendingSessionId) {
      navigateToSession(pendingSessionId);
    }
    setPendingSessionId(null);
  };

  /**
   * Cancels the pending session switch.
   */
  const handleCancelSwitch = () => {
    setPendingSessionId(null);
  };

  // Load sessions when sidebar opens
  useEffect(() => {
    if (isOpen) {
      loadSessions();
    }
  }, [isOpen, loadSessions]);

  /**
   * Opens the menu for a specific session.
   */
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    sessionId: string
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedSessionId(sessionId);
    posthog.capture("chatListMenuClick", { session_id: sessionId });
  };

  /**
   * Closes the menu.
   */
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedSessionId(null);
  };

  /**
   * Handles the delete action from the menu.
   * Opens the delete confirmation modal instead of deleting immediately.
   */
  const handleDeleteClick = () => {
    if (selectedSessionId) {
      setDeleteSessionId(selectedSessionId);
      posthog.capture("chatListMenuDeleteClick", {
        session_id: selectedSessionId,
      });
    }
    handleMenuClose();
  };

  /**
   * Confirms deletion of the selected session.
   * If the deleted session is the currently loaded chat, starts a new chat
   * (same as confirming "New Chat" in ChatPanel).
   */
  const handleConfirmDelete = async () => {
    if (deleteSessionId) {
      const isDeletingCurrentChat = deleteSessionId === currentSessionId;
      try {
        await handleDelete(deleteSessionId);
        posthog.capture("chatDeleteConfirmationClick", {
          session_id: deleteSessionId,
        });
        if (isDeletingCurrentChat) {
          onDeleteCurrentChat();
        }
      } catch (error) {
        console.error("Failed to delete chat:", error);
      }
    }
    setDeleteSessionId(null);
  };

  /**
   * Cancels the delete action.
   */
  const handleCancelDelete = () => {
    if (deleteSessionId) {
      posthog.capture("chatDeleteCancellationClick", {
        session_id: deleteSessionId,
      });
    }
    setDeleteSessionId(null);
  };

  return (
    <Slide direction="left" in={isOpen} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: "fixed",
          top: 54, // Below the navbar (54px height)
          right: 0,
          bottom: 0,
          width: 300,
          bgcolor: "background.paper",
          boxShadow: "-4px 0 10px rgba(0, 0, 0, 0.08)",
          clipPath: "inset(0px 0px -20px -20px)", // Clip top shadow, allow left/bottom
          zIndex: 1300, // Above everything including MUI modals
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Sidebar Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FolderIcon style={{ width: 20, height: 20 }} />
            <Typography
              sx={{
                color: "primary.main",
                fontSize: "1rem",
                fontWeight: 700,
              }}
            >
              Saved Chats
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "grey.600" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content Area */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {/* Loading State */}
          {isLoading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                py: 4,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error" sx={{ fontSize: "0.85rem" }}>
                {error}
              </Alert>
            </Box>
          )}

          {/* Empty State */}
          {!isLoading && !error && sessions.length === 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                py: 4,
              }}
            >
              <Typography color="text.secondary" fontSize="0.9rem">
                No saved chats yet
              </Typography>
            </Box>
          )}

          {/* Sessions List */}
          {!isLoading && !error && sessions.length > 0 && (
            <List disablePadding>
              {sessions.map((session: SessionSummary) => {
                const isSelected = session.id === currentSessionId;
                return (
                  <ListItemButton
                    key={session.id}
                    onClick={() => handleNavigateToSession(session.id)}
                    selected={isSelected}
                    sx={{
                      py: 1.25,
                      px: 2,
                      "&.Mui-selected": {
                        bgcolor: "grey.100",
                        "&:hover": {
                          bgcolor: "grey.100",
                        },
                      },
                      "&:hover": {
                        bgcolor: "grey.50",
                      },
                      "&:hover .menu-button": {
                        opacity: 1,
                      },
                      // Show menu button when selected
                      ...(isSelected && {
                        "& .menu-button": {
                          opacity: 1,
                        },
                      }),
                    }}
                    disableRipple
                  >
                    <ListItemText
                      primary={session.title || "Untitled Chat"}
                      primaryTypographyProps={{
                        fontSize: "0.9rem",
                        fontWeight: 400,
                        noWrap: true,
                        sx: { pr: 1 },
                      }}
                    />
                    <IconButton
                      className="menu-button"
                      size="small"
                      onClick={(e) => handleMenuOpen(e, session.id)}
                      disableRipple
                      sx={{
                        color: "grey.500",
                        opacity: 0,
                        transition: "opacity 0.2s",
                        "&:hover": {
                          color: "grey.700",
                          bgcolor: "grey.100",
                        },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem
            onClick={handleDeleteClick}
            disableRipple
            sx={{ color: "primary.main", gap: 1, pl: 1.5, pr: 3 }}
          >
            <DeleteIcon fontSize="small" sx={{ color: "primary.main" }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Delete Confirmation Modal */}
        <Dialog
          open={isDeleteModalOpen}
          onClose={handleCancelDelete}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle
            sx={{ pb: 0, display: "flex", justifyContent: "center" }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                bgcolor: "rgba(211, 47, 47, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 38, color: "error.main" }} />
            </Box>
          </DialogTitle>
          <DialogContent sx={{ textAlign: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Delete chat?
            </Typography>
            <Typography variant="body1" sx={{ color: "grey.600" }}>
              This will delete the selected chat including messages, responses,
              and sources.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <Button
              onClick={handleCancelDelete}
              variant="outlined"
              disableRipple
              disableElevation
              sx={{
                px: 3,
                py: 1,
                borderColor: "grey.300",
                color: "text.primary",
                "&:hover": { borderColor: "grey.400", bgcolor: "grey.50" },
              }}
            >
              No
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="primary"
              disableRipple
              disableElevation
              sx={{ px: 3, py: 1 }}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Modal for switching while generating */}
        <Dialog
          open={isConfirmModalOpen}
          onClose={handleCancelSwitch}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: "center" }}>Switch chat?</DialogTitle>
          <DialogContent sx={{ textAlign: "center" }}>
            <Typography variant="body1" sx={{ color: "grey.600" }}>
              Your response is still being generated. By switching to a Saved
              Chat this response will not be saved. Do you want to continue?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <Button
              onClick={handleCancelSwitch}
              variant="outlined"
              disableRipple
              disableElevation
              sx={{
                px: 3,
                py: 1,
                borderColor: "grey.300",
                color: "text.primary",
                "&:hover": { borderColor: "grey.400", bgcolor: "grey.50" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSwitch}
              variant="contained"
              color="primary"
              disableRipple
              disableElevation
              sx={{ px: 3, py: 1 }}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Slide>
  );
};

export default SavedChatsSidebar;
